import "dayjs/locale/bg";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/es";
import "dayjs/locale/fr";
import "dayjs/locale/hu";
import "dayjs/locale/it";
import "dayjs/locale/ja";
import "dayjs/locale/pt";
import "dayjs/locale/ro";
import "dayjs/locale/sk";
import "dayjs/locale/sr";
import dayjs from "dayjs";
import { Controller } from "react-hook-form";
import Datepicker from "react-tailwindcss-datepicker";
import type {
  DateType,
  DateValueType,
  DateRangeType,
  PopoverDirectionType,
} from "react-tailwindcss-datepicker/dist/types";
import { type Translationtype } from "utils";
import { Tooltip } from "../components/tooltip";

interface IFormDateInputProps {
  errors: any;
  control: any;
  name: string;
  className?: string;
  title: string;
  minDate?: any;
  maxDate?: any;
  tooltip?: string | null;
  useRange?: boolean;
  asSingle?: boolean;
  startWeekOn?: "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
  i18n?: string;
  displayFormat?: string;
  disabledDates?: DateRangeType[] | undefined | null;
  primaryColor?:
    | "purple"
    | "blue"
    | "orange"
    | "yellow"
    | "red"
    | "amber"
    | "lime"
    | "green"
    | "emerald"
    | "teal"
    | "cyan"
    | "sky"
    | "indigo"
    | "violet"
    | "fuchsia"
    | "pink"
    | "rose"
    | undefined;
  showShortcuts?: boolean;
  showFooter?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  t: Translationtype;
  popoverDirection?: PopoverDirectionType;
  isRequired?: boolean;
}

function toValidDate(value: unknown): DateType {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }
  if (typeof value === "object" && value !== null && "toDate" in value) {
    const maybeDate = (value as { toDate?: () => Date }).toDate?.();
    if (!maybeDate) return null;
    return Number.isNaN(maybeDate.getTime()) ? null : maybeDate;
  }
  return null;
}

function toDateRangeValue(value: unknown): DateValueType {
  if (!value) return { startDate: null, endDate: null };
  if (typeof value === "object" && value !== null && "startDate" in value) {
    const dateRange = value as { startDate?: unknown; endDate?: unknown };
    return {
      startDate: toValidDate(dateRange.startDate),
      endDate: toValidDate(dateRange.endDate),
    };
  }
  const singleDate = toValidDate(value);
  return { startDate: singleDate, endDate: singleDate };
}

export function FormDateInput({
  errors,
  control,
  name,
  title,
  minDate,
  maxDate,
  className,
  disabledDates,
  tooltip = null,
  useRange = false,
  asSingle = false,
  startWeekOn = "mon",
  i18n = "hu",
  displayFormat = "YYYY-MM-DD",
  primaryColor = "indigo",
  showShortcuts = false,
  showFooter = false,
  placeholder,
  readOnly = false,
  disabled,
  t,
  popoverDirection,
  isRequired = false,
}: IFormDateInputProps) {
  // locale(i18n);
  return (
    <div className={`${className || ""} w-full`}>
      <label htmlFor={name} className="block mb-1 text-textColor">
        {title}
        {isRequired ? <span className="text-red-500"> *</span> : null}
        {tooltip ? <Tooltip text={tooltip} /> : null}
      </label>
      <Controller
        control={control}
        name={name}
        rules={{
          validate: {
            required: (value) => {
              if (value === "SomeValue") return "Some Message";
              if (!value) return "*Required";
            },
          },
        }}
        render={({ field: { value, ...fieldProps } }) => {
          const formattedValue = toDateRangeValue(value);
          return (
            <div>
              <Datepicker
                disabled={disabled || false}
                onChange={(v: DateValueType) => {
                  const normalize = (d: unknown): string | null => {
                    if (!d) return null;
                    const p = dayjs(d as Parameters<typeof dayjs>[0]);
                    return p.isValid() ? p.format("YYYY-MM-DD") : null;
                  };
                  fieldProps.onChange({
                    startDate: normalize(v?.startDate),
                    endDate: normalize(v?.endDate),
                  });
                }}
                useRange={useRange}
                asSingle={asSingle}
                startFrom={formattedValue.startDate}
                startWeekOn={startWeekOn}
                i18n={i18n}
                displayFormat={displayFormat}
                primaryColor={primaryColor}
                showShortcuts={showShortcuts}
                showFooter={showFooter}
                placeholder={placeholder || t(`Global.datepickerPlaceholder`)}
                readOnly={readOnly}
                value={formattedValue}
                minDate={toValidDate(minDate)}
                maxDate={toValidDate(maxDate)}
                inputId={name}
                disabledDates={disabledDates}
                inputClassName="input  w-full  cursor-pointer"
                containerClassName="datepicker-wrapper relative w-full text-brown"
                popoverDirection={
                  popoverDirection ? popoverDirection : undefined
                }
              />
            </div>
          );
        }}
      />
      {!disabled && errors[name]?.message ? (
        <p className="text-sm my-3 text-error">
          {errors[name]?.message as any}
        </p>
      ) : null}
    </div>
  );
}
